import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import { visuallyHidden } from "@mui/utils";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import { skipToken } from "@reduxjs/toolkit/query";
import { MarketplaceResultType } from "config";
import MarketplaceChatCardContent from "marketplace/_views/marketplace-chat-card-content";
import MarketplaceProfileLink, { getMarketplaceProfilePath } from "marketplace/_views/marketplace-profile-link";
import MarketplaceResultCard, {
  MarketplaceResultAvatar,
  MarketplaceResultFact,
  MarketplaceResultFactList,
  MoreCount,
} from "marketplace/_views/marketplace-result-card";
import MarketplaceResultFooter from "marketplace/_views/marketplace-result-footer";
import { mergeFactLists } from "marketplace/_views/result-text";
import TeamResultMembers from "marketplace/_views/team-result-members";
import TeamBookmarkButton from "marketplace/bookmarks/_views/team-bookmark-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { MarketplaceIndividualInfoJSON } from "marketplace/_schema/marketplace-individual-info-schema";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import { useGetTeamAvatarQuery, useContactTeamMutation } from "marketplace/teams/_api/marketplace-team-api";
import { getTeamAvatar } from "marketplace/teams/_store/marketplace-teams-redux-slice";
import Team from "marketplace/teams/team";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import React from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import Session from "users/__session/session";
import { getDirectProposalTargetUrl } from "work/proposals/_views/_buttons/direct-proposal-button";

/** How many of a team's firms the details line names before the rest become a "+N". */
const LISTED_FIRMS = 2;

type TeamFirm = { id?: string | null; name: string };

/**
 * The firms a team's people belong to, leader first, each once. The search also sends
 * `associatedFirms`, but that is one entry per member — a firm of five appears five times.
 */
export function getTeamFirms(result: MarketplaceTeamInfoAPIResponse): TeamFirm[] {
  const firms: TeamFirm[] = [];
  for (const person of [result.leader, ...(result.members ?? [])]) {
    const name = person?.companyName?.trim();
    if (!name) continue;
    const key = person?.companyId ?? name;
    if (firms.some((firm) => (firm.id ?? firm.name) === key)) continue;
    firms.push({ id: person?.companyId, name });
  }
  return firms;
}

export interface TeamResultProps {
  result: MarketplaceTeamInfoAPIResponse;
  embedded: boolean;
  session: Session;
  navigate?: NavigateFunction;
  compactLayout?: boolean;
  getText: (fieldNames: string[], base: unknown, noCommas?: boolean) => string;
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth?: boolean,
  ) => void;
  onTeamSelected?: (team: Team) => void;
}

export default function TeamResult(props: Readonly<TeamResultProps>) {
  const { result, embedded, session, navigate, compactLayout, getText, handleResultClicked, onTeamSelected } = props;

  const teamId = result?.id ? new Guid(result.id) : null;
  const leaderId = result?.leader?.userId ? new Guid(result.leader.userId) : null;
  const avatarId = result.avatarId;
  const teamName = getText(["name"], result, true);

  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();

  // #region Selectors
  const cachedAvatar = useSelector(getTeamAvatar(teamId, "medium"));
  const { data: fetchedAvatar } = useGetTeamAvatarQuery(
    teamId && avatarId && !cachedAvatar ? { teamId: teamId.value, size: "medium" } : skipToken,
  );
  const avatar = cachedAvatar ?? fetchedAvatar;
  const [triggerContactTeam] = useContactTeamMutation();

  function openTeamProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(event, profileId, MarketplaceResultType.Team, <ViewTeamProfile teamId={profileId} />, true);
  }

  // The avatar repeats the name's link for the mouse. The name is the one keyboard stop, so the
  // avatar stays out of the tab order and out of the accessibility tree.
  function linkAvatarToProfile(avatarElements: React.ReactNode) {
    if (!result?.id) return avatarElements;
    const profileId = result.id;
    return (
      <MarketplaceProfileLink
        profileType={MarketplaceResultType.Team}
        profileId={profileId}
        onOpen={(event) => openTeamProfile(event, profileId)}
        tabIndex={-1}
        aria-hidden="true"
        underline="none"
        sx={{ display: "contents" }}
      >
        {avatarElements}
      </MarketplaceProfileLink>
    );
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function getPersonLink(person: MarketplaceIndividualInfoJSON | undefined) {
    const personId = person?.id;
    if (!personId) return null;
    return (
      <MarketplaceProfileLink
        profileType={MarketplaceResultType.Individual}
        profileId={personId}
        onOpen={(event) => openIndividualProfile(event, personId)}
        underline="hover"
        noWrap
      >
        {getText(["firstName", "lastName"], person, true)}
      </MarketplaceProfileLink>
    );
  }

  function getFirmLink(firm: TeamFirm) {
    const firmId = firm.id;
    if (!firmId) return firm.name;
    return (
      <MarketplaceProfileLink
        profileType={MarketplaceResultType.Company}
        profileId={firmId}
        onOpen={(event) => openCompanyProfile(event, firmId)}
        underline="hover"
      >
        {firm.name}
      </MarketplaceProfileLink>
    );
  }

  // In the roster "View all" opens: each member's name, then their firm in smaller text.
  function renderRosterMember(member: MarketplaceIndividualInfoJSON) {
    const companyId = member.companyId;
    return (
      <>
        {getPersonLink(member) ?? getText(["firstName", "lastName"], member, true)}
        {member.companyName && (
          <div style={{ fontSize: "0.85em", opacity: 0.7 }}>
            {companyId
              ? getFirmLink({ id: companyId, name: member.companyName })
              : member.companyName}
          </div>
        )}
      </>
    );
  }

  function getDetails(): React.ReactNode[] {
    const firms = getTeamFirms(result);
    const hiddenFirms = firms.length - LISTED_FIRMS;
    const memberCount = result.members?.length ?? 0;
    return [
      ...firms.slice(0, LISTED_FIRMS).map((firm) => getFirmLink(firm)),
      hiddenFirms > 0 && (
        <MoreCount>
          +{hiddenFirms}
          <span style={visuallyHidden}> more firms</span>
        </MoreCount>
      ),
      `${memberCount} ${memberCount === 1 ? "member" : "members"}`,
    ];
  }

  function getFacts(): MarketplaceResultFact[] {
    const facts: MarketplaceResultFact[] = [];
    const leaderLink = getPersonLink(result.leader);
    if (leaderLink) facts.push({ label: "Leader", value: leaderLink });
    if (result.members?.length) {
      facts.push({
        label: "Members",
        value: <TeamResultMembers members={result.members} renderMember={renderRosterMember} />,
      });
    }
    // A team profile holds only a name, a description and its people, so what the team can do is what
    // its people can do between them.
    const people = [result.leader, ...(result.members ?? [])];
    const coverage: [string, string[]][] = [
      ["Practice areas", mergeFactLists(people.map((person) => person?.practiceAreas))],
      ["Technical background", mergeFactLists(people.map((person) => person?.technicalBackgrounds))],
      ["Registered in", mergeFactLists(people.map((person) => person?.regionsRegistered))],
      ["Categories", mergeFactLists([result.categories])],
    ];
    for (const [label, items] of coverage) {
      if (items.length > 0) facts.push({ label, value: <MarketplaceResultFactList items={items} /> });
    }
    return facts;
  }

  async function contactTeam() {
    if (!result?.id) throw new Error("teamId is required to contact team.");
    try {
      const forumJson = await triggerContactTeam({ teamId: result.id }).unwrap();
      setForum(Forum.fromJSON(forumJson));
      setShowChat(true);
    } catch (error) {
      console.error(error);
    }
  }

  function getSelectButton(result: MarketplaceTeamInfoAPIResponse) {
    if (!onTeamSelected) return undefined;

    return (
      <Button
        variant="outlined"
        color="primary"
        aria-label="Select Team"
        startIcon={<AddIcon />}
        disableElevation
        onClick={() => onTeamSelected(result.deserializeToTeam())}
      >
        Select Team
      </Button>
    );
  }

  // Teams have no signing-authority check today, so Create proposal leads whenever proposals are
  // allowed at all.
  function showProposalButtons(): boolean {
    if (!navigate) return false;
    return result?.id !== undefined && result?.leader?.userId !== session?.user?.id?.value;
  }

  function startProposal() {
    const targetUrl = getDirectProposalTargetUrl({
      targetName: Name.create({
        params: { firstName: result.leader?.firstName ?? "", lastName: result.leader?.lastName ?? "" },
      }),
      targetTeamId: teamId ?? undefined,
      targetTeamName: result?.name,
    });
    if (targetUrl) navigate?.(targetUrl.pathname + targetUrl.search);
  }

  return (
    <React.Fragment>
      {showChat && (
        <MarketplaceChatCardContent>
          <ChatWindow fillAvailableSpace forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
        </MarketplaceChatCardContent>
      )}
      {!showChat && (
        <MarketplaceResultCard
          resultType={MarketplaceResultType.Team}
          label={teamName}
          compact={compactLayout}
          avatar={linkAvatarToProfile(
            <MarketplaceResultAvatar
              resultType={MarketplaceResultType.Team}
              name={teamName}
              src={avatar}
              compact={compactLayout}
            />,
          )}
          name={
            result?.id ? (
              <MarketplaceProfileLink
                profileType={MarketplaceResultType.Team}
                profileId={result.id}
                onOpen={(event) => openTeamProfile(event, result.id)}
                color="inherit"
                underline="always"
              >
                {teamName}
              </MarketplaceProfileLink>
            ) : (
              teamName
            )
          }
          details={getDetails()}
          headerAction={!embedded && <TeamBookmarkButton teamId={teamId ?? undefined} name={teamName} />}
          description={result.description}
          facts={getFacts()}
          picker={embedded ? getSelectButton(result) : undefined}
          footer={
            !embedded && (
              <MarketplaceResultFooter
                resultName={teamName}
                profilePath={getMarketplaceProfilePath(MarketplaceResultType.Team, result.id ?? "")}
                networkUserId={leaderId ?? undefined}
                onCreateProposal={showProposalButtons() ? startProposal : undefined}
                onSendMessage={contactTeam}
              />
            )
          }
        />
      )}
    </React.Fragment>
  );
}
